<template>
  <span v-if="hasJobs" class="sp-job-progress" :class="classModifiers" role="status">
    <span v-if="i18nReady" class="sp-job-progress__text">{{ message }}</span>
    <sp-progress v-if="!finished" class="sp-job-progress__indicator" type="ellipsis" size="x-small"></sp-progress>
  </span>
</template>

<script setup>
import { useDocumentVisibility, useTimeoutPoll } from "@vueuse/core";
import { computed, onScopeDispose, ref, watch } from "vue";
import { createApiClient } from "#/shared/api/client";
import { useHostElement } from "#ce/composables/useHostElement";
import { toJSON, toNumber, trimToUndefined } from "#ce/utils/props";
import { t, whenI18nReady } from "./i18n";
import { DEFAULT_INTERVAL_MS, props as jobProgressProps } from "./props.js";

const TERMINAL_STATUSES = new Set(["completed", "failed"]);

// Consecutive reads that fail before polling stops and the sentence says the progress cannot be
// checked. A read that answers resets the count.
const MAX_CONSECUTIVE_FAILED_READS = 5;

const props = defineProps(jobProgressProps);

const host = useHostElement();

// Retries are off because the next poll reads the endpoint again anyway.
const api = createApiClient({ parse: false, retries: 0 });

// Gates the first paint so the sentence does not flash its key before the strings are hydrated.
const i18nReady = ref(false);
whenI18nReady.then(() => {
  i18nReady.value = true;
});

const jobIds = computed(() => {
  const ids = toJSON(props.jobIds, true);

  return Array.isArray(ids) ? ids : [];
});
const hasJobs = computed(() => jobIds.value.length > 0);

const intervalMs = computed(() => {
  const value = toNumber(trimToUndefined(props.interval), DEFAULT_INTERVAL_MS);

  return value > 0 ? value : DEFAULT_INTERVAL_MS;
});

// Latest status per job id. A job the endpoint has not reported yet counts as pending.
const statuses = ref({});
const failedReads = ref(0);

function countWithStatus(status) {
  return jobIds.value.filter((id) => statuses.value[id] === status).length;
}

const total = computed(() => jobIds.value.length);
const completed = computed(() => countWithStatus("completed"));
const failed = computed(() => countWithStatus("failed"));
const settled = computed(() => jobIds.value.every((id) => TERMINAL_STATUSES.has(statuses.value[id])));
const unreachable = computed(() => failedReads.value >= MAX_CONSECUTIVE_FAILED_READS);
// Polling has ended, with an outcome or without one.
const finished = computed(() => settled.value || unreachable.value);

const message = computed(() => {
  const values = { done: completed.value, total: total.value, failed: failed.value };

  if (unreachable.value) {
    return t("unreachable", values, total.value);
  }

  if (!settled.value) {
    return t("in_progress", values, total.value);
  }

  if (failed.value > 0) {
    return t("failed", values, total.value);
  }

  return t("completed", values, total.value);
});

const classModifiers = computed(() => ({
  "--is-settled": settled.value,
  "--has-failure": settled.value && failed.value > 0,
  "--is-unreachable": unreachable.value,
}));

// An enclosing sp-alert takes the outcome as its intent once polling ends: success, error when a
// job failed, warning when the endpoint could not be reached. The admin flash renders the alert as
// info until then.
const alert = host?.closest("sp-alert") ?? null;
const initialAlertType = alert?.getAttribute("type") ?? "info";

function outcomeIntent() {
  if (unreachable.value) {
    return "warning";
  }

  return failed.value > 0 ? "error" : "success";
}

function handleFinished(ended) {
  if (ended && alert) {
    alert.setAttribute("type", outcomeIntent());
  }
}

watch(finished, handleFinished);

// One read at a time: the interval and the visibility path both call poll(), and a second
// request while one is pending could answer first and be overwritten by the older statuses.
// A call that finds a read pending asks for one more once it answers, so the current id set
// is always read.
let reading = false;
let rereadRequested = false;
// Bumped when the id set changes: a response to a read of the previous set is dropped.
let generation = 0;

function applyStatuses(jobs) {
  const next = { ...statuses.value };

  for (const job of Array.isArray(jobs) ? jobs : []) {
    next[job.id] = job.status;
  }

  statuses.value = next;
}

async function poll() {
  if (reading) {
    rereadRequested = true;
    return;
  }

  reading = true;
  const requestGeneration = generation;

  try {
    const jobs = await api.get(props.statusUrl, { ids: jobIds.value });

    // A body that is not the endpoint's list (an HTML page once the session expired) is a
    // failed read, not an empty one.
    if (!Array.isArray(jobs)) {
      throw new TypeError("The job status endpoint did not answer with a list");
    }

    if (requestGeneration === generation) {
      applyStatuses(jobs);
      failedReads.value = 0;
    }
  } catch {
    // The last statuses stay on screen until a read answers again.
    if (requestGeneration === generation) {
      failedReads.value += 1;
    }
  } finally {
    reading = false;
  }

  if (rereadRequested) {
    rereadRequested = false;

    if (!finished.value) {
      await poll();
      return;
    }
  }

  if (finished.value) {
    pause();
  }
}

const { pause, resume } = useTimeoutPoll(poll, intervalMs, { immediate: false });
const visibility = useDocumentVisibility();

// Set on unmount, so a read that answers after the element is gone starts no interval.
let disposed = false;

function markDisposed() {
  disposed = true;
}

onScopeDispose(markDisposed);

// Reads once, then arms the interval unless polling has ended or the tab is hidden.
async function readThenSchedule() {
  await poll();

  if (!disposed && !finished.value && visibility.value !== "hidden") {
    resume();
  }
}

if (hasJobs.value) {
  readThenSchedule();
}

// A new id set starts over: statuses of the old jobs are dropped, the enclosing alert returns to
// the intent it was rendered with, and polling restarts for the new ones, or stops when there are
// none.
function restartForJobIds() {
  pause();
  generation += 1;
  statuses.value = {};
  failedReads.value = 0;
  alert?.setAttribute("type", initialAlertType);

  if (hasJobs.value) {
    readThenSchedule();
  }
}

watch(() => jobIds.value.join(","), restartForJobIds);

// A hidden tab stops the polling; the tab coming back reads at once and arms the interval again.
function handleVisibilityChange(state) {
  if (!hasJobs.value || finished.value) {
    return;
  }

  if (state === "hidden") {
    pause();
  } else {
    // pause() drops the tick a read that was in flight when the tab went hidden may have scheduled.
    pause();
    readThenSchedule();
  }
}

watch(visibility, handleVisibilityChange);
</script>

<style>
:host {
  display: inline;
}
</style>

<style scoped lang="scss">
.sp-job-progress {
  display: inline-flex;
  align-items: baseline;
  gap: var(--sp-comp-job-progress-gap, var(--sp-ref-scale-4));
  color: var(--sp-comp-job-progress-color, inherit);

  &.--has-failure {
    color: var(--sp-comp-job-progress-failure-color, var(--sp-sys-color-error));
  }

  &.--is-unreachable {
    color: var(--sp-comp-job-progress-unreachable-color, var(--sp-sys-color-warning));
  }
}
</style>
